import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { COLLECTIONS, type CollectionKey } from '../api/client'
import { db } from '../cache/db'
import { CollectionTabs, type TabKey } from '../components/CollectionTabs'
import { ExportMenu } from '../components/ExportMenu'
import { Gallery } from '../components/Gallery'
import { ProfileBanner } from '../components/ProfileBanner'
import { ProgressOverlay } from '../components/ProgressOverlay'
import { PublicView } from '../components/PublicView'
import { RecordList, type PostFilter } from '../components/RecordList'
import { SubnavPortal } from '../components/TopBar'
import { useFeedLoader } from '../hooks/useFeedLoader'
import { normalizeHandle } from '../utils/handle'
import { useAuth } from '../store/auth'
import { usePullAll } from '../store/pullAll'
import { useRefresh } from '../store/refresh'
import { useSettings } from '../store/settings'
import { useTabs } from '../store/tabs'

const VALID: TabKey[] = [
  'posts',
  'replies',
  'reposts',
  'likes',
  'follows',
  'blocks',
  'lists',
  'packs',
  'feeds',
  'gallery',
]

function tabToCollection(tab: TabKey): CollectionKey {
  if (tab === 'gallery' || tab === 'replies') return 'posts'
  if (tab === 'posts') return 'posts'
  return tab
}

function tabToFilter(tab: TabKey): PostFilter {
  if (tab === 'replies') return 'replies'
  if (tab === 'posts') return 'posts'
  return 'all'
}

export function Profile() {
  const { handle, collection } = useParams<{ handle: string; collection?: string }>()
  const navigate = useNavigate()
  const service = useSettings((s) => s.service)
  const cleanHandle = normalizeHandle(handle ?? '', service)
  const tab: TabKey = (VALID as string[]).includes(collection ?? '')
    ? (collection as TabKey)
    : 'posts'

  const collectionTab: CollectionKey = tab === 'gallery' ? 'likes' : tabToCollection(tab)
  const loader = useFeedLoader(cleanHandle || null, collectionTab)
  const openTab = useTabs((s) => s.open)
  const setLastTab = useTabs((s) => s.setLastTab)
  const refreshTrigger = useRefresh((s) => s.counter)
  const pullAllState = usePullAll((s) => s.state)
  const startPull = usePullAll((s) => s.pull)
  const accessJwt = useAuth((s) => s.session?.accessJwt)
  const [nameFilter, setNameFilter] = useState('')

  // Reset the name filter whenever profile or tab changes — it's only relevant
  // to the current follows/blocks view.
  useEffect(() => {
    setNameFilter('')
  }, [cleanHandle, tab])

  useEffect(() => {
    if (cleanHandle) openTab(cleanHandle)
  }, [cleanHandle, openTab])

  // Persist the current sub-tab so jumping between profiles preserves it.
  useEffect(() => {
    setLastTab(tab)
  }, [tab, setLastTab])

  // Auto-refresh latest on every tab switch, on every handle switch, and when
  // the global refresh button is hit. Always pulls newer-than-cached, never
  // re-fetches what we already have.
  useEffect(() => {
    if (tab === 'gallery') return
    if (!cleanHandle) return
    if (loader.loading) return
    loader.fetchSinglePage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanHandle, tab, refreshTrigger])

  // Auto-refresh visible loader rows after pull-everything finishes
  useEffect(() => {
    if (
      !pullAllState.running &&
      pullAllState.startedAt &&
      pullAllState.handle === cleanHandle
    ) {
      loader.refreshFromCache()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullAllState.running, pullAllState.handle])

  // Auto-pull on first profile visit: if nothing is cached for this handle and
  // a pull isn't already in flight, kick one off so the user just waits once
  // and from then on the site is instant. Pull also runs the hydration phase
  // (media + sensitivity labels) so the gallery is accurate without a second
  // scan.
  useEffect(() => {
    if (!cleanHandle) return
    if (pullAllState.running) return
    let cancelled = false
    void (async () => {
      const cached = await db.records.where('handle').equals(cleanHandle).count()
      if (cancelled) return
      if (cached === 0) startPull(cleanHandle, service, accessJwt)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanHandle])

  if (!cleanHandle) {
    return <div className="mx-auto mt-10 max-w-2xl px-4 text-ink-300">No handle.</div>
  }

  return (
    <>
      <SubnavPortal>
        <CollectionTabs value={tab} onChange={(k) => navigate(`/profile/${cleanHandle}/${k}`)} />
      </SubnavPortal>
      <ProfileBanner handle={cleanHandle} />

      {tab !== 'gallery' && tab !== 'feeds' && tab !== 'lists' && tab !== 'packs' ? (
        <div className="mx-auto mt-3 max-w-6xl px-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-300">
            <button
              type="button"
              className="glass-button"
              onClick={() => loader.loadAll()}
              disabled={loader.loading}
              title="walk this collection from where the cache left off all the way back through history"
            >
              ⤓ pull all {tab}
            </button>
            <button
              type="button"
              className="glass-button text-xs"
              onClick={() => loader.clearCache()}
              disabled={loader.loading}
              title={`clear cached ${tab} for this profile (records only — pull-everything will refetch)`}
            >
              reset {tab}
            </button>
            <button
              type="button"
              className="glass-button text-xs"
              onClick={async () => {
                if (
                  !confirm(
                    `Wipe cached ${tab} for @${cleanHandle} and pull them all again from scratch?`,
                  )
                ) {
                  return
                }
                await loader.clearCache()
                await loader.loadAll()
              }}
              disabled={loader.loading}
              title={`wipe cached ${tab} and immediately re-pull every record from the PDS`}
              style={{
                borderColor: 'var(--accent-strong)',
                color: 'var(--accent-strong)',
              }}
            >
              ⟳ reset + re-pull {tab}
            </button>
            <span className="ml-auto">
              {loader.count.toLocaleString()} cached • newest{' '}
              <span className="font-mono">{loader.newestRkey ?? '—'}</span>
            </span>
            <ExportMenu
              handle={cleanHandle}
              collection={COLLECTIONS[collectionTab]}
              count={loader.count}
            />
          </div>
          {tab === 'follows' || tab === 'blocks' ? (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder={`filter ${tab} by handle…`}
                className="w-full max-w-md rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-ink-400 focus:border-[color:var(--accent-strong)] focus:outline-none"
              />
              {nameFilter ? (
                <button
                  type="button"
                  className="glass-button text-xs"
                  onClick={() => setNameFilter('')}
                  title="clear filter"
                >
                  clear
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {loader.error && tab !== 'gallery' ? (
        <div className="mx-auto mt-4 max-w-6xl px-4">
          <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            {loader.error}
          </div>
        </div>
      ) : null}

      {tab === 'gallery' ? (
        <Gallery key={cleanHandle} handle={cleanHandle} />
      ) : tab === 'feeds' || tab === 'lists' || tab === 'packs' ? (
        <PublicView key={cleanHandle} handle={cleanHandle} kind={tab} />
      ) : (
        <RecordList
          rows={loader.rows}
          postFilter={tabToFilter(tab)}
          nameFilter={tab === 'follows' || tab === 'blocks' ? nameFilter : ''}
        />
      )}

      <ProgressOverlay info={loader.progress} onCancel={loader.cancel} />
    </>
  )
}
